import { Component, ErrorInfo, ReactNode } from "react";
import * as Sentry from "@sentry/react";

type ErrorBoundaryProps = {
  children: ReactNode;
  /** Rendered instead of the default error UI when a child throws. */
  fallback?: ReactNode;
};

type ErrorBoundaryState = {
  hasError: boolean;
  /**
   * Set ONLY after Sentry confirms delivery of THIS error's event: the
   * client's afterSendEvent hook fires for the same event id with a 2xx
   * status. captureException returns a generated id before anything is sent,
   * so its return value alone proves nothing; a blocked, failed or not yet
   * confirmed send leaves this null (D-202, #1566).
   */
  eventId: string | null;
};

function isDelivered(response: { statusCode?: number } | undefined): boolean {
  const status = response?.statusCode;
  return typeof status === "number" && status >= 200 && status < 300;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, eventId: null };

  private stopWatchingDelivery: (() => void) | undefined;

  static getDerivedStateFromError(): Partial<ErrorBoundaryState> {
    return { hasError: true };
  }

  componentDidCatch(err: unknown, info: ErrorInfo): void {
    console.error("UI ErrorBoundary", err, info);
    // React does not rethrow boundary-caught errors in production builds, so
    // Sentry's global handlers never see them. Report explicitly, and only
    // when a client is actually configured to receive the event.
    const client = Sentry.getClient();
    if (!client?.getDsn()) return;

    // Subscribe before capturing so the confirmation cannot be missed. The
    // transport resolves `{}` on a failed or blocked send, which is not 2xx.
    let capturedId: string | null = null;
    this.stopWatchingDelivery?.();
    this.stopWatchingDelivery = client.on("afterSendEvent", (event, response) => {
      if (capturedId === null || event?.event_id !== capturedId) return;
      this.stopWatching();
      if (isDelivered(response)) this.setState({ eventId: capturedId });
    });

    let returned: unknown;
    try {
      returned = Sentry.captureException(err, {
        contexts: { react: { componentStack: info?.componentStack ?? null } },
      });
    } catch {
      // A failed capture has no id to show; the error UI still renders.
      this.stopWatching();
      return;
    }
    if (typeof returned === "string" && returned.trim() !== "") {
      capturedId = returned;
    } else {
      this.stopWatching();
    }
  }

  componentWillUnmount(): void {
    this.stopWatching();
  }

  private stopWatching(): void {
    this.stopWatchingDelivery?.();
    this.stopWatchingDelivery = undefined;
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;

    const { eventId } = this.state;
    return (
      <div data-testid="error-boundary" role="alert" className="p-6 max-w-lg">
        <h1 className="text-lg font-semibold text-gray-900">Something went wrong</h1>
        <p className="mt-1 text-sm text-gray-600">
          This page hit an unexpected error. You can go back or reload to try again.
        </p>
        {eventId ? (
          <p className="mt-2 text-xs text-gray-500">
            Error reference: <code>{eventId}</code>
          </p>
        ) : null}
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            data-testid="error-go-back"
            onClick={() => window.history.back()}
            className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
          >
            Go back
          </button>
          <button
            type="button"
            data-testid="error-reload"
            onClick={() => window.location.reload()}
            className="rounded-md bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
          >
            Reload
          </button>
        </div>
      </div>
    );
  }
}
